import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { triggerSave } from "../core/save/pendingSave";
import { useAuth } from "../auth/AuthContext";
import { API } from "../services/api";
import ChangePasswordModal from "../components/ChangePasswordModal";
import GlobalSearchPalette from "../components/GlobalSearchPalette";

const PRIMARY_NAV_GROUP = [
    { type: "link", path: "/",                        label: "Gestion des commandes", abbr: "Co", badgeKey: "unreadMails" },
    { type: "link", path: "/validation-pharma",       label: "Valid. pharmaceutique", abbr: "VP", badgeKey: "aValider" },
    { type: "link", path: "/robot-production",        label: "Robot",                 abbr: "Ro", badgeKey: "robotPending" },
    { type: "link", path: "/etiquetage-production",   label: "Étiquetage",            abbr: "Ét", badgeKey: "etiquettagePending" },
    { type: "link", path: "/bon-livraison",           label: "Bon de livraison",      abbr: "BL" },
    { type: "link", path: "/livreur",                 label: "Espace Livreur",        abbr: "LV" },
];

const SECONDARY_NAV_GROUP = [
    { type: "link", path: "/hors-pilulier",           label: "Hors pilulier",         abbr: "HP" },
    { type: "link", path: "/primo",                   label: "Primo",                 abbr: "Pm" },
    { type: "link", path: "/cno",                     label: "CNO",                   abbr: "CN" },
    { type: "link", path: "/reclamation",             label: "Réclamations",          abbr: "Rc" },
];

const SUIVI_NAV_ITEM = { type: "link", path: "/suivi", label: "Suivi", abbr: "Su" };
const INFORMATIONS_NAV_ITEM = { type: "link", path: "/informations", label: "Informations", abbr: "In" };
const ADMINISTRATION_NAV_ITEM = { type: "link", path: "/administration", label: "Administration", abbr: "Ad" };
const ESPACE_PERSONNEL_NAV_ITEM = { type: "link", path: "/espace-personnel", label: "Espace personnel", abbr: "EP" };
const SUIVI_CONFORMITE_NAV_ITEM = { type: "link", path: "/suivi-conformite", label: "Suivi conformité", abbr: "SC" };
const NAV_GROUP_LABELS = ["Commandes et production", "Activités complémentaires", "Suivi et gestion"];
const ROBOT_SUB_NAV_ITEMS = [
    { path: "/robot-production", label: "Urgences" },
    { path: "/robot-planning", label: "Planning" },
];
const BON_LIVRAISON_SUB_NAV_ITEMS = [
    { path: "/bon-livraison", label: "Bons" },
    { path: "/bon-livraison/historique", label: "Historique" },
    { path: "/bon-livraison/planning", label: "Planning livraison" },
];
const INFORMATION_SUB_NAV_ITEMS = [
    { path: "/informations?tab=utilisateurs", label: "Utilisateurs", roles: ["TITULAIRE", "ADMIN", "SUPER_ADMIN"] },
    { path: "/informations?tab=ehpads", label: "EHPADs", roles: ["TITULAIRE", "ADMIN", "PHARMACIEN", "SUPER_ADMIN"] },
    { path: "/informations?tab=tournees", label: "Tournées", roles: ["TITULAIRE", "ADMIN", "PHARMACIEN", "SUPER_ADMIN"] },
    { path: "/informations?tab=livret-therapeutique", label: "Livret thérapeutique", roles: ["TITULAIRE", "ADMIN", "PHARMACIEN", "SUPER_ADMIN"] },
    { path: "/informations?tab=maintenance", label: "Maintenance", roles: ["SUPER_ADMIN"] },
];

const ROLE_LABELS   = { SUPER_ADMIN: "Super Admin", TITULAIRE: "Titulaire", ADMIN: "Admin", PHARMACIEN: "Pharmacien", PRODUCTEUR: "Producteur", LIVREUR: "Livreur" };
const ROLE_INITIALS = { SUPER_ADMIN: "SA", TITULAIRE: "TI", ADMIN: "AD", PHARMACIEN: "PH", PRODUCTEUR: "PR", LIVREUR: "LV" };

function initialsForService(name) {
    const words = String(name || "Service").trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return String(words[0] || "S").slice(0, 2).toUpperCase();
}

function tourneeMenuStyle(width) {
    return {
        position: "fixed",
        left: `${width + 6}px`,
        top: "72px",
        width: "190px",
        padding: "8px",
        borderRadius: "10px",
        background: "#0C3B3C",
        border: "1px solid #2E5C58",
        boxShadow: "0 16px 40px rgba(12, 59, 60, 0.28)",
        zIndex: 1000,
    };
}

function sideMenuStyle(width, top) {
    return {
        position: "fixed",
        left: `${width + 6}px`,
        top: `${top}px`,
        width: "190px",
        padding: "8px",
        borderRadius: "10px",
        background: "#0C3B3C",
        border: "1px solid #2E5C58",
        boxShadow: "0 16px 40px rgba(12, 59, 60, 0.28)",
        zIndex: 1000,
    };
}

function tourneeNavButtonStyle(active) {
    return {
        display: "block",
        width: "100%",
        padding: "9px 10px",
        borderRadius: "6px",
        border: "none",
        background: active ? "#F4F1EA" : "transparent",
        color: active ? "#0C3B3C" : "#F4F1EA",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: active ? 800 : 600,
        textAlign: "left",
        transition: "background 0.15s ease, color 0.15s ease",
    };
}

export default function Navbar({ mobileOpen = false, onMobileClose = () => {} }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, selectedPharmacy, selectPharmacy } = useAuth();
    const { isMobile, isLarge } = useBreakpoint();

    const isSuperAdmin  = user?.role === "SUPER_ADMIN";
    const isTitulaire   = user?.role === "TITULAIRE";
    const isAdmin       = user?.role === "ADMIN";
    const isPharmacien  = user?.role === "PHARMACIEN";
    const isProducteur  = user?.role === "PRODUCTEUR";
    const isLivreur     = user?.role === "LIVREUR";

    const [pharmacies, setPharmacies]               = useState([]);
    const [services, setServices]                    = useState([]);
    const [badges, setBadges]                        = useState({});
    const [currentPharmacyConfig, setCurrentPharmacyConfig] = useState(null);
    const [tournees, setTournees]                    = useState([]);
    const [tourneeMenuOpen, setTourneeMenuOpen]      = useState(false);
    const [robotMenuOpen, setRobotMenuOpen]          = useState(false);
    const [robotMenuTop, setRobotMenuTop]            = useState(72);
    const [bonLivraisonMenuOpen, setBonLivraisonMenuOpen] = useState(false);
    const [bonLivraisonMenuTop, setBonLivraisonMenuTop]   = useState(72);
    const [informationMenuOpen, setInformationMenuOpen] = useState(false);
    const [informationMenuTop, setInformationMenuTop]   = useState(72);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showUserMenu, setShowUserMenu]             = useState(false);
    const [foldedNavGroups, setFoldedNavGroups]       = useState({ 1: true });
    const [collapsed, setCollapsed]                  = useState(() => {
        try { return localStorage.getItem("sidebar-collapsed") === "true"; } catch { return false; }
    });
    const userMenuRef = useRef(null);
    const robotMenuCloseTimerRef = useRef(null);
    const bonLivraisonMenuCloseTimerRef = useRef(null);
    const informationMenuCloseTimerRef = useRef(null);

    useEffect(() => {
        try { localStorage.setItem("sidebar-collapsed", collapsed); } catch {}
    }, [collapsed]);

    useEffect(() => () => {
        if (robotMenuCloseTimerRef.current) clearTimeout(robotMenuCloseTimerRef.current);
        if (bonLivraisonMenuCloseTimerRef.current) clearTimeout(bonLivraisonMenuCloseTimerRef.current);
        if (informationMenuCloseTimerRef.current) clearTimeout(informationMenuCloseTimerRef.current);
    }, []);

    useEffect(() => {

        const onClickOutside = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target))
                setShowUserMenu(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (isSuperAdmin)
            API.get("/super-admin/pharmacies").then(r => setPharmacies(r.data)).catch(() => {});
    }, [isSuperAdmin]);

    useEffect(() => {
        if (isProducteur || isLivreur) {
            setServices([]);
            return;
        }
        API.get("/admin/services", { noRedirectOn401: true })
            .then(r => setServices(r.data || []))
            .catch(() => setServices([]));
    }, [isProducteur, isLivreur, selectedPharmacy?.id]);

    useEffect(() => {
        if (isProducteur || isLivreur) {
            setCurrentPharmacyConfig(null);
            setTournees([]);
            return;
        }
        let mounted = true;
        API.get("/admin/pharmacy", { noRedirectOn401: true })
            .then(({ data }) => { if (mounted) setCurrentPharmacyConfig(data || null); })
            .catch(() => { if (mounted) setCurrentPharmacyConfig(null); });
        API.get("/ehpads/tournees", { noRedirectOn401: true })
            .then(({ data }) => { if (mounted) setTournees(data || []); })
            .catch(() => { if (mounted) setTournees([]); });
        return () => { mounted = false; };
    }, [isProducteur, isLivreur, selectedPharmacy?.id]);

    useEffect(() => {
        if (isLivreur) return undefined;

        const fetchBadges = () => API.get("/stats/badges", { noRedirectOn401: true }).then(r => setBadges(r.data)).catch(() => {});
        fetchBadges();
        const interval = setInterval(fetchBadges, 60_000);
        return () => clearInterval(interval);
    }, [isLivreur, location.pathname]);

    const hasAdministrationAccess = isTitulaire || isAdmin || isPharmacien;

    const serviceNavItems = services
        .filter(service => ["MEDICAL_EQUIPMENT", "CONTROLE", "DOSSIER_LOT_PRODUCTION"].includes(service.type) && service.active)
        .map(service => ({
            type: "link",
            path: `/services/${service.serviceKey}`,
            label: service.name || "Service",
            abbr: initialsForService(service.name),
        }));

    const secondaryGroup = serviceNavItems.length > 0
        ? [...SECONDARY_NAV_GROUP, ...serviceNavItems]
        : SECONDARY_NAV_GROUP;

    const navGroups = isProducteur
        ? [[
            { type: "link", path: "/robot-production", label: "Robot", abbr: "Ro", badgeKey: "robotPending" },
        ]]
        : isLivreur
            ? [[{ type: "link", path: "/livreur", label: "Espace Livreur", abbr: "LV" }]]
        : isSuperAdmin
            ? [
                PRIMARY_NAV_GROUP,
                secondaryGroup,
                [
                    SUIVI_NAV_ITEM,
                    INFORMATIONS_NAV_ITEM,
                    ADMINISTRATION_NAV_ITEM,
                    ESPACE_PERSONNEL_NAV_ITEM,
                    SUIVI_CONFORMITE_NAV_ITEM,
                    { type: "link", path: "/super-admin", label: "Super Admin",    abbr: "SA" },
                ],
            ]
            : hasAdministrationAccess
                ? [PRIMARY_NAV_GROUP, secondaryGroup, [
                    SUIVI_NAV_ITEM,
                    INFORMATIONS_NAV_ITEM,
                    ADMINISTRATION_NAV_ITEM,
                    ESPACE_PERSONNEL_NAV_ITEM,
                    ...(isTitulaire ? [SUIVI_CONFORMITE_NAV_ITEM] : []),
                ]]
                : [PRIMARY_NAV_GROUP, secondaryGroup, [SUIVI_NAV_ITEM]];

    const handleNav = async (path) => {
        if (isMobile) onMobileClose();
        if (location.pathname === path && !(location.search || "")) return;
        await triggerSave();
        navigate(path);
    };

    const toggleNavGroup = (groupIndex) => {
        setFoldedNavGroups(current => ({
            ...current,
            [groupIndex]: !current[groupIndex],
        }));
    };

    const selectedTournee = new URLSearchParams(location.search || "").get("tournee");
    const showCommandeTourneeMenu = currentPharmacyConfig?.commandeTourneeShortcutEnabled && tournees.length > 1;

    const openRobotMenu = (target) => {
        if (robotMenuCloseTimerRef.current) clearTimeout(robotMenuCloseTimerRef.current);
        const rect = target.getBoundingClientRect();
        setRobotMenuTop(Math.max(72, rect.top));
        setRobotMenuOpen(true);
    };

    const openBonLivraisonMenu = (target) => {
        if (bonLivraisonMenuCloseTimerRef.current) clearTimeout(bonLivraisonMenuCloseTimerRef.current);
        const rect = target.getBoundingClientRect();
        setBonLivraisonMenuTop(Math.max(72, rect.top));
        setBonLivraisonMenuOpen(true);
    };

    const openInformationMenu = (target) => {
        if (informationMenuCloseTimerRef.current) clearTimeout(informationMenuCloseTimerRef.current);
        const rect = target.getBoundingClientRect();
        setInformationMenuTop(Math.max(72, rect.top));
        setInformationMenuOpen(true);
    };

    const keepRobotMenuOpen = () => {
        if (robotMenuCloseTimerRef.current) clearTimeout(robotMenuCloseTimerRef.current);
        setRobotMenuOpen(true);
    };

    const keepBonLivraisonMenuOpen = () => {
        if (bonLivraisonMenuCloseTimerRef.current) clearTimeout(bonLivraisonMenuCloseTimerRef.current);
        setBonLivraisonMenuOpen(true);
    };

    const keepInformationMenuOpen = () => {
        if (informationMenuCloseTimerRef.current) clearTimeout(informationMenuCloseTimerRef.current);
        setInformationMenuOpen(true);
    };

    const scheduleRobotMenuClose = () => {
        if (robotMenuCloseTimerRef.current) clearTimeout(robotMenuCloseTimerRef.current);
        robotMenuCloseTimerRef.current = setTimeout(() => setRobotMenuOpen(false), 180);
    };

    const scheduleBonLivraisonMenuClose = () => {
        if (bonLivraisonMenuCloseTimerRef.current) clearTimeout(bonLivraisonMenuCloseTimerRef.current);
        bonLivraisonMenuCloseTimerRef.current = setTimeout(() => setBonLivraisonMenuOpen(false), 180);
    };

    const scheduleInformationMenuClose = () => {
        if (informationMenuCloseTimerRef.current) clearTimeout(informationMenuCloseTimerRef.current);
        informationMenuCloseTimerRef.current = setTimeout(() => setInformationMenuOpen(false), 180);
    };

    const handleCommandeTourneeNav = async (tournee) => {
        if (isMobile) onMobileClose();
        const target = tournee ? `/?tournee=${encodeURIComponent(tournee)}` : "/";
        const current = `${location.pathname}${location.search || ""}`;
        if (current === target) return;
        await triggerSave();
        navigate(target);
        setTourneeMenuOpen(false);
    };

    const handleRobotNav = async (path) => {
        if (isMobile) onMobileClose();
        if (location.pathname === path && !(location.search || "")) {
            setRobotMenuOpen(false);
            return;
        }
        await triggerSave();
        navigate(path);
        setRobotMenuOpen(false);
    };

    const handleBonLivraisonNav = async (path) => {
        if (isMobile) onMobileClose();
        if (location.pathname === path && !(location.search || "")) {
            setBonLivraisonMenuOpen(false);
            return;
        }
        await triggerSave();
        navigate(path);
        setBonLivraisonMenuOpen(false);
    };

    const handleInformationNav = async (path) => {
        if (isMobile) onMobileClose();
        const current = `${location.pathname}${location.search || ""}`;
        if (current === path) {
            setInformationMenuOpen(false);
            return;
        }
        await triggerSave();
        navigate(path);
        setInformationMenuOpen(false);
    };

    const handleLogout = async () => {
        await triggerSave();
        logout();
        navigate("/login", { replace: true });
    };

    const handlePharmacyChange = (e) => {
        const id = e.target.value;
        if (!id) selectPharmacy(null);
        else selectPharmacy(pharmacies.find(p => String(p.id) === id) ?? null);
    };

    const roleLabel    = ROLE_LABELS[user?.role]   ?? user?.role ?? "";
    const roleInitials = ROLE_INITIALS[user?.role] ?? (user?.role?.slice(0, 2).toUpperCase() ?? "?");
    const W = isMobile ? (isLarge ? 280 : 240) : (collapsed ? 56 : (isLarge ? 256 : 224));

    const mobileStyle = isMobile ? {
        position: "fixed",
        top: 0, left: 0, bottom: "auto",
        height: "100svh",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.22s ease",
        zIndex: 200,
        boxShadow: mobileOpen ? "4px 0 24px rgba(0,0,0,0.35)" : "none",
    } : {
        transition: "width 0.18s ease, min-width 0.18s ease",
        zIndex: 100,
    };

    return (
        <aside aria-label="Navigation principale" style={{
            width: W,
            minWidth: W,
            height: isMobile ? "100svh" : "100vh",
            background: "#0C3B3C",
            borderRight: "1px solid #2E5C58",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            overflow: "hidden",
            ...mobileStyle,
        }}>
            {/* Brand + toggle */}
            <div style={{
                minHeight: "72px",
                display: "flex",
                alignItems: "center",
                padding: collapsed ? "0 10px" : "8px 12px 8px 14px",
                borderBottom: "2px solid #17B978",
                flexShrink: 0,
                gap: "8px",
                justifyContent: collapsed ? "center" : "space-between",
            }}>
                <div style={{ display: "flex", alignItems: "center", minWidth: 0, overflow: "hidden" }}>
                    <img
                        src={collapsed ? "/symbole-couleur.svg" : "/logotype-horizontal-blanc.svg"}
                        alt={collapsed ? "SUIVI QUALITÉ" : "SUIVI QUALITÉ — conseil, audit, certification"}
                        style={collapsed ? {
                            width: 36,
                            height: 36,
                            padding: 5,
                            borderRadius: 9,
                            background: "#F4F1EA",
                            flexShrink: 0,
                        } : {
                            display: "block",
                            width: 150,
                            height: "auto",
                            flexShrink: 0,
                        }}
                    />
                </div>
                <button
                    onClick={() => setCollapsed(v => !v)}
                    title={collapsed ? "Développer" : "Réduire"}
                    style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#B9C6C2", fontSize: "17px",
                        padding: "5px 7px", borderRadius: "6px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, lineHeight: 1,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "white"}
                    onMouseLeave={e => e.currentTarget.style.color = "#B9C6C2"}
                >
                    {collapsed ? "›" : "‹"}
                </button>
            </div>

            {!isProducteur && !isLivreur && (
                <GlobalSearchPalette compact={collapsed} onNavigate={isMobile ? onMobileClose : undefined} />
            )}

            {/* Nav items */}
            <nav style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "8px 0" }}>
                {navGroups.map((group, groupIndex) => (
                    <div key={groupIndex}>
                        {navGroups.length > 1 && (
                        <button
                            type="button"
                            onClick={() => toggleNavGroup(groupIndex)}
                            aria-expanded={!foldedNavGroups[groupIndex]}
                            aria-controls={`nav-group-${groupIndex}`}
                            aria-label={`${foldedNavGroups[groupIndex] ? "Déplier" : "Replier"} ${NAV_GROUP_LABELS[groupIndex] || "Navigation"}`}
                            title={collapsed ? NAV_GROUP_LABELS[groupIndex] || "Navigation" : undefined}
                            style={{
                                width: "100%",
                                minHeight: "30px",
                                padding: collapsed ? "5px 0" : "7px 14px 5px",
                                border: "none",
                                borderTop: groupIndex > 0 ? "1px solid #2E5C58" : "none",
                                background: "transparent",
                                color: "#93A8A3",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: collapsed ? "center" : "space-between",
                                fontSize: "10px",
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                lineHeight: 1.2,
                                textAlign: "left",
                                textTransform: "uppercase",
                            }}
                        >
                            {!collapsed && <span>{NAV_GROUP_LABELS[groupIndex] || "Navigation"}</span>}
                            <span
                                aria-hidden="true"
                                style={{
                                    color: "#17B978",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                    transform: foldedNavGroups[groupIndex] ? "rotate(-90deg)" : "rotate(0deg)",
                                    transition: "transform 0.15s ease",
                                }}
                            >
                                ⌄
                            </span>
                        </button>
                        )}
                        <div id={`nav-group-${groupIndex}`} hidden={Boolean(foldedNavGroups[groupIndex])}>
                        {group.map((item) => {
                                if (item.type === "link") {
                                    const isRobotNav = item.path === "/robot-production";
                                    const isBonLivraisonNav = item.path === "/bon-livraison";
                                    const isInformationNav = item.path === "/informations";
                                    const active = isRobotNav
                                        ? ["/robot-production", "/robot-planning"].includes(location.pathname)
                                        : isBonLivraisonNav
                                            ? ["/bon-livraison", "/bon-livraison/historique", "/bon-livraison/planning"].includes(location.pathname)
                                        : isInformationNav
                                            ? location.pathname === "/informations"
                                        : location.pathname === item.path;
                                const informationSubNavItems = INFORMATION_SUB_NAV_ITEMS.filter(subItem => subItem.roles.includes(user?.role));
                                const count = item.badgeKey ? (badges[item.badgeKey] ?? 0) : 0;
                                const showBadge = count > 0;
                                const isCommandeNav = item.path === "/";
                                return (
                                    <div
                                        key={item.path}
                                        onMouseEnter={e => {
                                                if (isCommandeNav && showCommandeTourneeMenu) setTourneeMenuOpen(true);
                                                if (isRobotNav) openRobotMenu(e.currentTarget);
                                                if (isBonLivraisonNav) openBonLivraisonMenu(e.currentTarget);
                                                if (isInformationNav) openInformationMenu(e.currentTarget);
                                            }}
                                            onMouseLeave={() => {
                                                if (isCommandeNav) setTourneeMenuOpen(false);
                                                if (isRobotNav) scheduleRobotMenuClose();
                                                if (isBonLivraisonNav) scheduleBonLivraisonMenuClose();
                                                if (isInformationNav) scheduleInformationMenuClose();
                                            }}
                                        style={{ position: "relative" }}
                                    >
                                    <button
                                        onClick={() => handleNav(item.path)}
                                        title={collapsed ? item.label : undefined}
                                        style={{
                                            width: "100%",
                                            padding: collapsed ? "9px 0" : "9px 12px",
                                            background: active ? "rgba(244, 241, 234, 0.12)" : "none",
                                            border: "none",
                                            borderLeft: `3px solid ${active ? "#17B978" : "transparent"}`,
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            justifyContent: collapsed ? "center" : "flex-start",
                                            color: active ? "#FFFFFF" : "#B9C6C2",
                                            fontSize: "13px",
                                            fontWeight: active ? 700 : 500,
                                            whiteSpace: "nowrap",
                                            textAlign: "left",
                                            transition: "background 0.1s, color 0.1s",
                                        }}
                                        onMouseEnter={e => {
                                                if (isCommandeNav && showCommandeTourneeMenu) setTourneeMenuOpen(true);
                                                if (isRobotNav) openRobotMenu(e.currentTarget);
                                                if (isBonLivraisonNav) openBonLivraisonMenu(e.currentTarget);
                                                if (isInformationNav) openInformationMenu(e.currentTarget);
                                                if (!active) { e.currentTarget.style.background = "rgba(244, 241, 234, 0.08)"; e.currentTarget.style.color = "#FFFFFF"; }
                                            }}
                                        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#B9C6C2"; } }}
                                    >
                                        <span style={{ position: "relative", flexShrink: 0 }}>
                                            <span style={{
                                                width: "26px", height: "26px", borderRadius: "6px",
                                                background: active ? "#F4F1EA" : "rgba(244, 241, 234, 0.08)",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: "10px", fontWeight: 700,
                                                color: active ? "#0C3B3C" : "#B9C6C2",
                                                border: `1px solid ${active ? "#17B978" : "#2E5C58"}`,
                                            }}>{item.icon || item.abbr}</span>
                                            {showBadge && (
                                                <span style={{
                                                    position: "absolute", top: "-5px", right: "-5px",
                                                    background: "#ef4444", color: "white",
                                                    borderRadius: "999px",
                                                    fontSize: "9px", fontWeight: 700, lineHeight: 1,
                                                    padding: count > 99 ? "2px 3px" : "2px 4px",
                                                    minWidth: "14px", textAlign: "center",
                                                    border: "1.5px solid #0C3B3C",
                                                }}>
                                                    {count > 99 ? "99+" : count}
                                                </span>
                                            )}
                                                {((isCommandeNav && showCommandeTourneeMenu) || isRobotNav || isBonLivraisonNav || isInformationNav) && collapsed && (
                                                <span
                                                    aria-hidden="true"
                                                    style={{
                                                        position: "absolute",
                                                        right: "-9px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        color: active ? "#17B978" : "#B9C6C2",
                                                        fontSize: "15px",
                                                        fontWeight: 800,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    ›
                                                </span>
                                            )}
                                        </span>
                                        {!collapsed && (
                                            <>
                                                <span>{item.label}</span>
                                                {isCommandeNav && showCommandeTourneeMenu && (
                                                    <span
                                                        aria-hidden="true"
                                                        style={{
                                                            marginLeft: "auto",
                                                            color: active ? "#17B978" : "#B9C6C2",
                                                            fontSize: "16px",
                                                            fontWeight: 800,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        ›
                                                    </span>
                                                )}
                                                    {isRobotNav && (
                                                        <span
                                                        aria-hidden="true"
                                                        style={{
                                                            marginLeft: "auto",
                                                            color: active ? "#17B978" : "#B9C6C2",
                                                            fontSize: "16px",
                                                            fontWeight: 800,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        ›
                                                        </span>
                                                    )}
                                                    {isBonLivraisonNav && (
                                                        <span
                                                            aria-hidden="true"
                                                            style={{
                                                                marginLeft: "auto",
                                                                color: active ? "#17B978" : "#B9C6C2",
                                                                fontSize: "16px",
                                                                fontWeight: 800,
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            ›
                                                        </span>
                                                    )}
                                                    {isInformationNav && (
                                                        <span
                                                            aria-hidden="true"
                                                            style={{
                                                                marginLeft: "auto",
                                                                color: active ? "#17B978" : "#B9C6C2",
                                                                fontSize: "16px",
                                                                fontWeight: 800,
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                        ›
                                                        </span>
                                                )}
                                            </>
                                        )}
                                    </button>
                                        {isInformationNav && informationMenuOpen && informationSubNavItems.length > 0 && (
                                            <div
                                            role="menu"
                                            tabIndex={-1}
                                            aria-label="Informations"
                                            onMouseEnter={keepInformationMenuOpen}
                                            onMouseLeave={scheduleInformationMenuClose}
                                            style={sideMenuStyle(W, informationMenuTop)}
                                        >
                                            {informationSubNavItems.map(informationItem => (
                                                <button
                                                    key={informationItem.path}
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={() => handleInformationNav(informationItem.path)}
                                                    style={tourneeNavButtonStyle(`${location.pathname}${location.search || ""}` === informationItem.path)}
                                                >
                                                    {informationItem.label}
                                                </button>
                                            ))}
                                            </div>
                                        )}
                                        {isBonLivraisonNav && bonLivraisonMenuOpen && (
                                            <div
                                                role="menu"
                                                tabIndex={-1}
                                                aria-label="Bon de livraison"
                                                onMouseEnter={keepBonLivraisonMenuOpen}
                                                onMouseLeave={scheduleBonLivraisonMenuClose}
                                                style={sideMenuStyle(W, bonLivraisonMenuTop)}
                                            >
                                                {BON_LIVRAISON_SUB_NAV_ITEMS.map(bonLivraisonItem => (
                                                    <button
                                                        key={bonLivraisonItem.path}
                                                        type="button"
                                                        role="menuitem"
                                                        onClick={() => handleBonLivraisonNav(bonLivraisonItem.path)}
                                                        style={tourneeNavButtonStyle(location.pathname === bonLivraisonItem.path)}
                                                    >
                                                        {bonLivraisonItem.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {isRobotNav && robotMenuOpen && (
                                            <div
                                            role="menu"
                                            tabIndex={-1}
                                            aria-label="Robot"
                                            onMouseEnter={keepRobotMenuOpen}
                                            onMouseLeave={scheduleRobotMenuClose}
                                            style={sideMenuStyle(W, robotMenuTop)}
                                        >
                                            {ROBOT_SUB_NAV_ITEMS.map(robotItem => (
                                                <button
                                                    key={robotItem.path}
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={() => handleRobotNav(robotItem.path)}
                                                    style={tourneeNavButtonStyle(location.pathname === robotItem.path)}
                                                >
                                                    {robotItem.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {isCommandeNav && showCommandeTourneeMenu && tourneeMenuOpen && (
                                        <div
                                            role="menu"
                                            aria-label="Tournées gestion des commandes"
                                            style={tourneeMenuStyle(W)}
                                        >
                                            {tournees.map(tournee => (
                                                <button
                                                    key={tournee.id || tournee.name}
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={() => handleCommandeTourneeNav(tournee.name)}
                                                    style={tourneeNavButtonStyle(selectedTournee === tournee.name)}
                                                >
                                                    {tournee.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    </div>
                                );
                            }

                            return null;
                        })}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Bottom: pharmacy selector (super admin) + user */}
            <div style={{ borderTop: "1px solid #2E5C58", padding: "6px 0", flexShrink: 0 }}>
                {isSuperAdmin && !collapsed && (
                    <div style={{ padding: "6px 14px 10px" }}>
                        <div style={{ fontSize: "10px", color: "#B9C6C2", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>
                            Pharmacie
                        </div>
                        <select
                            value={selectedPharmacy?.id ?? ""}
                            onChange={handlePharmacyChange}
                            style={{
                                width: "100%", padding: "5px 8px", borderRadius: "6px",
                                border: "1px solid #5F7F79", background: "#164E4A",
                                color: "#F4F1EA", fontSize: "12px", cursor: "pointer",
                            }}
                        >
                            <option value="">— Toutes —</option>
                            {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}

                {user && (
                    <div ref={userMenuRef} style={{ position: "relative" }}>
                        <button
                            onClick={() => setShowUserMenu(v => !v)}
                            title={roleLabel}
                            style={{
                                width: "100%",
                                padding: collapsed ? "9px 0" : "9px 12px",
                                background: showUserMenu ? "rgba(244, 241, 234, 0.10)" : "none",
                                border: "none",
                                borderLeft: "3px solid transparent",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                justifyContent: collapsed ? "center" : "flex-start",
                                color: "#B9C6C2",
                                fontSize: "13px",
                                whiteSpace: "nowrap",
                                transition: "background 0.1s, color 0.1s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(244, 241, 234, 0.10)"; e.currentTarget.style.color = "#FFFFFF"; }}
                            onMouseLeave={e => { if (!showUserMenu) { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#B9C6C2"; } }}
                        >
                            <div style={{
                                width: "26px", height: "26px", borderRadius: "50%",
                                background: "#F4F1EA", color: "#0C3B3C",
                                border: "1px solid #17B978",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "10px", fontWeight: 700, flexShrink: 0,
                            }}>
                                {roleInitials}
                            </div>
                            {!collapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{roleLabel}</span>}
                        </button>

                        {showUserMenu && (
                            <div style={{
                                position: "absolute",
                                bottom: "calc(100% + 6px)",
                                left: collapsed ? "calc(100% + 4px)" : "8px",
                                right: collapsed ? "auto" : "8px",
                                background: "#FFFFFF",
                                borderRadius: "10px",
                                boxShadow: "0 12px 32px rgba(12, 59, 60, 0.22)",
                                border: "1px solid #D8D2C6",
                                minWidth: "160px",
                                zIndex: 1000,
                                overflow: "hidden",
                            }}>
                                <div style={{
                                    padding: "10px 14px", borderBottom: "1px solid #E8E2D8",
                                    fontSize: "11px", color: "#5A6D68",
                                    fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                                }}>
                                    {roleLabel}
                                </div>
                                <button
                                    onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}
                                    style={{
                                        display: "block", width: "100%", textAlign: "left",
                                        padding: "9px 14px", background: "none", border: "none",
                                        cursor: "pointer", fontSize: "13px", color: "#122A26",
                                        borderBottom: "1px solid #E8E2D8",
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#F4F1EA"}
                                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                                >
                                    Mot de passe
                                </button>
                                <button
                                    onClick={() => { setShowUserMenu(false); handleLogout(); }}
                                    style={{
                                        display: "block", width: "100%", textAlign: "left",
                                        padding: "9px 14px", background: "none", border: "none",
                                        cursor: "pointer", fontSize: "13px", color: "#ef4444",
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                                >
                                    Déconnexion
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showChangePassword && (
                <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
            )}
        </aside>
    );
}
